/**
 * Phase 14.5 Step 2 — Sensitivity section (Phase 11).
 *
 * Variance-based first-order Saltelli S1 OR one-at-a-time response
 * curves. Reads the live pk-campaigns registry and surfaces a method
 * radio + samples-per-variable input + variable-subset multi-select
 * (chip toggles). Total-runs readout enforced against the 5000 cap
 * the backend enforces for sensitivity campaigns.
 */
import { useMemo } from "react";
import { GitBranch } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const METHOD_OPTIONS = [
    { id: "variance_first_order", label: "Variance first-order (Saltelli S1)" },
    { id: "one_at_a_time",        label: "One-at-a-time (OAT)" },
];

const TOTAL_RUNS_CAP = 5000;

function _allVarIds(registry) {
    if (!registry) return [];
    const arr = registry.variables || registry.entries || registry.catalog || [];
    if (!Array.isArray(arr)) return [];
    return arr.map((v) => v?.id).filter(Boolean);
}

export default function SensitivitySection({
    enabled = false,
    onToggleEnabled,
    value,
    onChange,
    registry,
}) {
    const allIds = useMemo(() => _allVarIds(registry), [registry]);
    const method = value?.method || "variance_first_order";
    const samplesPerVariable = Number(value?.samples_per_variable ?? 20);
    const subset = Array.isArray(value?.variable_subset) ? value.variable_subset : [];
    const effectiveCount = subset.length > 0 ? subset.length : allIds.length;
    const totalRuns = Math.max(0, samplesPerVariable * effectiveCount);
    const overCap = totalRuns > TOTAL_RUNS_CAP;

    const setField = (k, v) => onChange?.({ ...(value || {}), [k]: v });
    const toggleSubsetVar = (id) => {
        const next = subset.includes(id) ? subset.filter((x) => x !== id) : [...subset, id];
        setField("variable_subset", next);
    };

    return (
        <section
            className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4"
            data-testid="section-sensitivity"
            data-enabled={enabled ? "1" : "0"}
        >
            <div className="flex items-start gap-3">
                <Switch
                    checked={enabled}
                    onCheckedChange={onToggleEnabled}
                    data-testid="toggle-sensitivity"
                    aria-label="Enable sensitivity"
                />
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <GitBranch className="size-4 text-ux-text-3" />
                        <span className="text-sm font-medium text-ux-text-1">Sensitivity analysis</span>
                    </div>
                    <p className="text-xs text-ux-text-3">
                        Variance-based first-order indices with bootstrap CI, or per-variable
                        OAT response curves with per-sweep-point Wilson CI.
                    </p>
                </div>
            </div>

            {enabled && (
                <div className="mt-4 space-y-4" data-testid="sensitivity-details">
                    <div>
                        <Label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                            Method
                        </Label>
                        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Sensitivity method">
                            {METHOD_OPTIONS.map((opt) => (
                                <button
                                    key={opt.id}
                                    type="button"
                                    role="radio"
                                    aria-checked={method === opt.id}
                                    data-testid={`sensitivity-method-${opt.id}`}
                                    onClick={() => setField("method", opt.id)}
                                    className={[
                                        "rounded-full border px-3 py-1 text-xs transition-colors",
                                        method === opt.id
                                            ? "border-ux-text-1 bg-ux-surface-2 text-ux-text-1"
                                            : "border-ux-border-1 bg-ux-surface-1 text-ux-text-3 hover:bg-ux-surface-2/60",
                                    ].join(" ")}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                            <Label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                                Samples per variable
                            </Label>
                            <Input
                                type="number"
                                min={2}
                                max={200}
                                value={samplesPerVariable}
                                onChange={(e) => setField("samples_per_variable", parseInt(e.target.value || "0", 10) || 0)}
                                data-testid="sensitivity-samples-per-variable"
                            />
                        </div>
                        <div>
                            <Label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                                Variable subset
                            </Label>
                            <p className="text-xs text-ux-text-3">
                                {subset.length > 0
                                    ? `${subset.length} of ${allIds.length} selected.`
                                    : `All ${allIds.length} variables (default).`}
                            </p>
                        </div>
                    </div>
                    <div
                        className={[
                            "rounded-md border px-3 py-2 text-xs",
                            overCap
                                ? "border-amber-500/50 bg-amber-500/10 text-amber-300"
                                : "border-ux-border-1 bg-ux-surface-2/40 text-ux-text-3",
                        ].join(" ")}
                        data-testid="sensitivity-total-runs"
                        data-over-cap={overCap ? "1" : "0"}
                    >
                        Total runs: <strong className="text-ux-text-2">{totalRuns.toLocaleString()}</strong>{" "}
                        ({samplesPerVariable} × {effectiveCount}) · cap {TOTAL_RUNS_CAP.toLocaleString()}
                        {overCap && (
                            <span className="ml-2">— exceeds 5000-run cap; reduce samples or trim subset.</span>
                        )}
                    </div>
                    {allIds.length > 0 && (
                        <details className="text-xs">
                            <summary className="cursor-pointer text-ux-text-3" data-testid="sensitivity-subset-toggle">
                                Customise variable subset
                            </summary>
                            <div
                                className="mt-2 grid grid-cols-2 gap-1 sm:grid-cols-3"
                                data-testid="sensitivity-subset-grid"
                            >
                                {allIds.map((id) => (
                                    <button
                                        key={id}
                                        type="button"
                                        onClick={() => toggleSubsetVar(id)}
                                        data-testid={`sensitivity-subset-chip-${id}`}
                                        className={[
                                            "truncate rounded border px-2 py-1 text-left",
                                            subset.includes(id)
                                                ? "border-ux-text-1 bg-ux-surface-2 text-ux-text-1"
                                                : "border-ux-border-1 bg-ux-surface-1 text-ux-text-3 hover:bg-ux-surface-2/60",
                                        ].join(" ")}
                                    >
                                        {id}
                                    </button>
                                ))}
                            </div>
                        </details>
                    )}
                </div>
            )}
        </section>
    );
}
